import { Bot, User } from "lucide-react"

interface TranscriptionTabProps {
  audioResult?: any
}

export function TranscriptionTab({ audioResult }: TranscriptionTabProps) {
  // Extract transcript from audioResult
  // The transcript format from the API should be: Array<{speaker: string, text: string}>
  const transcript = audioResult?.transcript || []

  // Convert transcript entries to display format
  // Check if transcript exists and is a non-empty array
  const hasTranscript = transcript && Array.isArray(transcript) && transcript.length > 0
  
  const messages = hasTranscript
    ? transcript.map((entry: any, index: number) => {
        // Handle both direct properties and nested objects
        const speaker = entry?.speaker || entry?.speaker?.toString() || ""
        const text = entry?.text || entry?.text?.toString() || ""
        
        // Map speaker values: "agent", "assistant" -> "AI Agent", "user" -> "Patient"
        let speakerLabel = "Patient"
        if (speaker === "agent" || speaker === "assistant") {
          speakerLabel = "AI Agent"
        } else if (speaker === "user" || speaker === "patient") {
          speakerLabel = "Patient"
        } else if (speaker) {
          // Try to capitalize first letter as fallback
          speakerLabel = speaker.charAt(0).toUpperCase() + speaker.slice(1)
        }
        
        return {
          speaker: speakerLabel,
          text: text,
          timestamp: entry.timestamp ? formatTime(entry.timestamp) : "",
        }
      })
    : [
        // Fallback message if transcript is not available
        {
          speaker: "Info",
          text: "No transcript available for this audio. The transcript may still be processing.",
          timestamp: "",
        },
      ]

  return (
    <div className="space-y-6">
      {messages.map((message: any, idx: number) => {
        // Handle info message differently
        if (message.speaker === "Info") {
          return (
            <div key={idx} className="flex items-center justify-center p-6 bg-muted/50 rounded-lg border border-border">
              <p className="text-sm text-muted-foreground text-center">{message.text}</p>
            </div>
          )
        }

        return (
          <div key={idx} className={`flex gap-4 ${message.speaker === "Patient" ? "flex-row-reverse" : ""}`}>
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                message.speaker === "AI Agent" ? "bg-blue-100" : "bg-gray-200"
              }`}
            >
              {message.speaker === "AI Agent" ? (
                <Bot className="w-5 h-5 text-blue-600" />
              ) : (
                <User className="w-5 h-5 text-gray-600" />
              )}
            </div>

            <div className={`flex-1 ${message.speaker === "Patient" ? "text-right" : ""}`}>
              <div className={`flex items-center gap-2 mb-1 ${message.speaker === "Patient" ? "justify-end" : ""}`}>
                <span className="font-bold text-foreground text-sm">{message.speaker}</span>
                {message.timestamp && (
                  <span className="text-muted-foreground text-xs">{message.timestamp}</span>
                )}
              </div>
              <div
                className={`inline-block px-4 py-3 rounded-lg ${
                  message.speaker === "AI Agent" ? "bg-blue-50 text-foreground" : "bg-gray-100 text-foreground"
                }`}
              >
                <p className="text-sm leading-relaxed text-left">{message.text}</p>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, "0")}`
}

